import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  CheckCircle,
  XCircle,
  Camera,
  Loader2,
  AlertCircle,
  User,
  Calendar,
  Hash,
  DollarSign,
  Mail,
  Phone,
  person,
  Ticket as TicketIcon,
} from "lucide-react";
import { is } from "zod/v4/locales";

const Scanner = () => {
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
  const [scannedData, setScannedData] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [marking, setMarking] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  const html5QrcodeRef = useRef(null);
  const isMountedRef = useRef(true);
  const isProcessingRef = useRef(false);
  // const lastOrderIdRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    startScanner();

    return () => {
      isMountedRef.current = false;
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      if (isScanning || html5QrcodeRef.current) return;
      console.log("Starting scanner...");
      setCameraError(null);

      // Wait for DOM
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (!isMountedRef.current) return;

      const html5Qrcode = new Html5Qrcode("qr-reader");
      html5QrcodeRef.current = html5Qrcode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      await html5Qrcode.start(
        { facingMode: "environment" }, // Use back camera on mobile
        config,
        (decodedText, decodedResult) => {
          console.log("QR Code detected:", decodedText);
          onScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors - they're normal
        },
      );

      setIsScanning(true);
      console.log("Scanner started successfully");
    } catch (err) {
      console.error("Error starting scanner:", err);
      setCameraError(
        err.message || "Failed to start camera. Please check permissions.",
      );
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
        console.log("Scanner stopped");
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
      html5QrcodeRef.current = null;
      setIsScanning(false);
    }
  };

  const onScanSuccess = async (decodedText) => {
    if (isProcessingRef.current) return;
    console.log("Processing scanned QR:", decodedText);

    let scannedOrderId = decodedText;

    // If library gives an object directly
    if (decodedText && typeof decodedText === "object" && decodedText.orderId) {
      scannedOrderId = decodedText.orderId;
    }

    // If it is a JSON string
    if (typeof decodedText === "string") {
      try {
        const parsed = JSON.parse(decodedText);
        if (parsed && typeof parsed === "object" && parsed.orderId) {
          scannedOrderId = parsed.orderId;
        }
      } catch (parseError) {
        // If decodedText is not JSON, keep as-is
      }
    }

    // If QR contains a URL, pick last path segment as orderId
    if (typeof scannedOrderId === "string" && scannedOrderId.includes("/")) {
      const parts = scannedOrderId.split("/").filter(Boolean);
      scannedOrderId = parts[parts.length - 1];
    }

    if (!scannedOrderId) return;

    if (!scannedOrderId) {
      return;
    }
    // lastOrderIdRef.current = scannedOrderId;
    isProcessingRef.current = false;

    // Stop scanner only when we are going to hit the API
    await stopScanner();

    setScannedData(scannedOrderId);
    setLoading(true);
    setError(null);

    // Fetch order details
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/orders/${scannedOrderId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Order not found or invalid QR code");
      }

      if (data.success) {
        setOrderDetails(data.order);
      } else {
        throw new Error(data.message || "Failed to verify ticket");
      }
    } catch (err) {
      setError(err.message || "Failed to verify ticket");
      console.error("Error fetching order:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttended = async () => {
    if (!orderDetails) return;

    setMarking(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/orders/mark-attendance/${orderDetails._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to mark as attended");
      }

      if (data.success) {
        setSuccess(true);
        setOrderDetails((prev) =>
          prev ? { ...prev, status: "attended" } : prev,
        );
        // Keep scanner stopped until admin starts next scan
      } else {
        throw new Error(data.message || "Failed to mark attendance");
      }
    } catch (err) {
      setError(err.message || "Failed to mark attendance");
      console.error("Error marking attendance:", err);
    } finally {
      setMarking(false);
    }
  };

  const resetScanner = async () => {
    console.log("Resetting scanner...");

    // Clear states
    setScannedData(null);
    setOrderDetails(null);
    setError(null);
    setSuccess(false);
    setMarking(false);
    setCameraError(null);

    // Restart scanner
    await startScanner();
    isProcessingRef.current = false;
    // Keep lastOrderId to prevent re-scan of the same QR in this session
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B3C5C] via-[#2D5A7B] to-[#1B3C5C] p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-4">
            <TicketIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Event Check-In Scanner
          </h1>
          <p className="text-white/80">
            Scan attendee QR codes to verify and mark attendance
          </p>
        </div>

        {/* Camera Error */}
        {cameraError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <div>
                <p className="font-semibold">Camera Error</p>
                <p className="text-sm">{cameraError}</p>
                <button
                  onClick={startScanner}
                  className="text-sm underline mt-2"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scanner Section */}
        {!scannedData && !loading && (
          <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Camera className="h-6 w-6 text-[#F4A696]" />
              <h2 className="text-xl font-bold text-[#1B3C5C]">
                {isScanning
                  ? "Position QR Code in Frame"
                  : "Starting Camera..."}
              </h2>
            </div>

            {/* QR Scanner Container */}
            <div className="relative mx-auto max-w-md">
              <div
                id="qr-reader"
                className="rounded-xl overflow-hidden"
                style={{ aspectRatio: "1 / 1" }}
              ></div>

              {!isScanning && !cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
                  <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-[#F4A696] mx-auto mb-4" />
                    <p className="text-gray-600">Initializing camera...</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 p-4 bg-[#FFF4F0] border border-[#F4A696]/30 rounded-lg">
              <p className="text-sm text-[#1B3C5C] text-center">
                💡 <strong>Tip:</strong> Hold the QR code steady within the
                scanning area for best results
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <Loader2 className="h-16 w-16 text-[#F4A696] animate-spin mx-auto mb-4" />
            <p className="text-lg font-semibold text-[#1B3C5C]">
              Verifying ticket...
            </p>
            <p className="text-sm text-gray-600 mt-2">Please wait</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#1B3C5C] mb-2">
                Verification Failed
              </h2>
              <p className="text-red-600 text-lg">{error}</p>
            </div>

            <button
              onClick={resetScanner}
              className="w-full bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Scan Another Ticket
            </button>
          </div>
        )}

        {/* Already Scanned */}
        {orderDetails &&
          (orderDetails.scanned || orderDetails.status === "attended") &&
          !success &&
          !loading && (
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-4">
                  <AlertCircle className="h-12 w-12 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#1B3C5C] mb-2">
                  Already Checked In
                </h2>
                <p className="text-yellow-600 font-medium">
                  This ticket has already been scanned
                </p>
              </div>

              <OrderDetailsCard order={orderDetails} />

              <button
                onClick={resetScanner}
                className="w-full mt-6 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Scan Another Ticket
              </button>
            </div>
          )}

        {/* Valid Ticket */}
        {orderDetails &&
          !orderDetails.scanned &&
          orderDetails.status !== "attended" &&
          !success &&
          !loading && (
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#1B3C5C] mb-2">
                  Valid Ticket ✅
                </h2>
                <p className="text-gray-600">
                  Review details and mark attendance
                </p>
              </div>

              <OrderDetailsCard order={orderDetails} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <button
                  onClick={resetScanner}
                  disabled={marking}
                  className="bg-gray-200 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkAttended}
                  disabled={marking}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {marking ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Marking...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      Mark Attended
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

        {/* Success */}
        {success && (
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-4 animate-bounce">
                <CheckCircle className="h-16 w-16 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-[#1B3C5C] mb-2">
                Attendance Marked! 🎉
              </h2>
              <p className="text-gray-900 text-lg font-semibold mb-2">
                {orderDetails?.fullName}
              </p>
              <p className="text-green-600 font-semibold">
                Successfully checked in
              </p>

              <button
                onClick={resetScanner}
                className="mt-6 w-full bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Scan Next Ticket
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style jsx global>{`
        #qr-reader {
          border: none !important;
          width: 100% !important;
          height: 100% !important;
          background: #0f172a;
        }

        #qr-reader video {
          border-radius: 12px;
          width: 100% !important;
          max-width: 100% !important;
          height: 100% !important;
          object-fit: cover;
          object-position: center center;
        }

        #qr-reader__dashboard_section {
          display: none !important;
        }

        #qr-reader__scan_region {
          border-radius: 12px !important;
          width: 100% !important;
          height: 100% !important;
          background: transparent !important;
        }

        #qr-reader__scan_region video {
          filter: none !important;
          opacity: 1 !important;
        }

        #qr-reader__scan_region canvas,
        #qr-reader__scan_region img {
          opacity: 0 !important;
        }

        #qr-reader__dashboard_section_csr {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

// Order Details Card Component
const OrderDetailsCard = ({ order }) => {
  const user =
    order?.userId && typeof order.userId === "object" ? order.userId : null;
  const isGuest = Boolean(order?.isGuestBooking);
  const profilePicture = user?.profilePicture || order?.profilePicture || "";
  return (
    <div className="bg-gradient-to-br from-[#FFF4F0] to-[#FFE5DC] rounded-xl p-4 sm:p-6 space-y-4 border border-[#F4A696]/20">
      {/* Attendee Info */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-200">
          {profilePicture ? (
            <img
              src={profilePicture}
              alt="User"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">
              No Photo
            </div>
          )}
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Attendee Name
          </p>
          <p className="text-lg font-bold text-[#1B3C5C]">{order.fullName}</p>
        </div>
      </div>

      {/* Email */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Mail className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Email
          </p>
          <p className="text-sm font-semibold text-[#1B3C5C] break-all">
            {order.email}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <User className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Gender
          </p>
          <p className="text-sm font-semibold text-[#1B3C5C] break-all">
            {user?.gender || order?.gender || "N/A"}
          </p>
        </div>
      </div>

      {/* User Details */}
      {user && (
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-200">
            {user.profilePicture ? (
              <img
                src={user.profilePicture}
                alt="User"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">
                No Photo
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Profession
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span>{user.profession || "N/A"}</span>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Address
            </p>
            <div className="flex flex-wrap items-center gap-2">
                           <span>{user.city || 'N/A'}{user.country ? `, ${user.country}` : ''}</span>
            </div>
          </div>
          
        </div>
      )}

      {/* Phone */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <Phone className="h-5 w-5 text-green-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Phone
          </p>
          <p className="text-sm font-semibold text-[#1B3C5C]">{order.phone}</p>
        </div>
      </div>

      {/* Guest Booking */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <User className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Guest Booking
          </p>
          <p className="text-sm font-semibold text-[#1B3C5C]">
            {isGuest ? "Yes" : "No"}
          </p>
        </div>
      </div>

      {/* Event Name */}
      {(() => {
        const event = order.eventDetails || order.eventId;
        if (!event || typeof event === "string") return null;
        return (
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-shrink-0 w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-pink-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                Event
              </p>
              <p className="text-sm font-semibold text-[#1B3C5C]">
                {event.title}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {new Date(event.date).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}{" "}
                • {event.time}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Order ID */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
          <Hash className="h-5 w-5 text-yellow-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Order ID
          </p>
          <p className="text-sm font-mono font-semibold text-[#1B3C5C]">
            {order.orderId}
          </p>
        </div>
      </div>

      {/* Amount & Quantity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign className="h-5 w-5 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Amount
            </p>
            <p className="text-lg font-bold text-[#1B3C5C]">
              {order.currency} {order.amount}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Hash className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Quantity
            </p>
            <p className="text-lg font-bold text-[#1B3C5C]">
              {order.quantity} Ticket(s)
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <User className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Ticket Booked
            </p>
            <p className="text-lg font-bold text-[#1B3C5C]">
              {order.gender} Ticket(s)
            </p>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="pt-4 border-t border-[#F4A696]/20">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Status
          </span>
          <span
            className={`px-3 py-1 rounded-full text-xs font-bold ${
              order.scanned || order.status === "attended"
                ? "bg-yellow-100 text-yellow-700"
                : order.status === "paid"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-700"
            }`}
          >
            {order.scanned || order.status === "attended"
              ? "Already Scanned"
              : order.status.toUpperCase()}
          </span>
        </div>
      </div>
   
    </div>
  );
};

export default Scanner;
